<template>
  <div class="container profile-detail-block event-block">
    <div
      class="event-card"
      :class="{ clickable: clickable }"
      @click="handleClick"
    >
      <div class="event-info">
        <!-- Row 1: Icon and Company Names -->
        <div class="event-row event-row-companies">
          <div class="event-icon-box">
            <img src="@/assets/images/Group.svg" alt="Company" class="event-icon" />
          </div>
          <div class="event-companies">
            <span
              v-for="(company, idx) in event.companies"
              :key="company._id"
              class="company-badge"
            >
              {{ company.name }}
            </span>
          </div>
        </div>
        
        <!-- Row 2: Event Title -->
        <div class="event-row event-row-title">
          <p class="event-name">{{ event.event_name }}</p>
        </div>
        
        <!-- Row 3: Date, Time, Deadline and Status -->
        <div class="event-row event-row-bottom">
          <div class="event-datetime-wrapper">
            <i class="fa fa-calendar event-calendar-icon" aria-hidden="true"></i>
            <span
              class="event-date-time"
              v-if="useMemoize"
              v-memoize:formatEventDateTime="[event.event_date.from, event.event_date.to]"
            ></span>
            <span class="event-date-time" v-else>
              {{ formatEventDateTime(event.event_date.from, event.event_date.to) }}
            </span>
            
            <span class="event-separator">｜</span>
            
            <span
              class="event-deadline-text"
              v-if="useMemoize"
              v-memoize:formatApplicationDeadline="[
                event.application_period.to,
              ]"
            >
            </span>
            <span class="event-deadline-text" v-else>{{
              formatApplicationDeadline(event.application_period.to)
            }}</span>
          </div>
          
          <span
            :class="getApplicationButtonClass(event)"
            class="application-status-badge"
            @click.stop
          >
            {{ getApplicationButtonText(event) }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import eventHelpers from '../mixins/eventHelpers';

export default {
  name: 'EventCard',
  mixins: [eventHelpers],
  props: {
    event: {
      type: Object,
      required: true,
    },
    clickable: {
      type: Boolean,
      default: true,
    },
    useMemoize: {
      type: Boolean,
      default: false,
    },
    showMobileLayout: {
      type: Boolean,
      default: true,
    },
  },
  directives: {
    memoize: {
      bind(el, binding, vnode) {
        const cache = new Map();
        const key = JSON.stringify(binding.value);
        if (!cache.has(key)) {
          cache.set(key, vnode.context[binding.arg](...binding.value));
        }
        el.textContent = cache.get(key);
      },
    },
  },
  computed: {
    ...mapGetters({
      profile: 'getProfile',
    }),
  },
  methods: {
    handleClick() {
      if (this.clickable && this.$listeners.click) {
        this.$emit('click', this.event._id);
      }
    },
    handleButtonClick() {
      if (this.$listeners.buttonClick) {
        this.$emit('buttonClick', this.event);
      } else if (this.clickable) {
        this.handleClick();
      }
    },
  },
};
</script>

<style scoped>
.event-block {
  position: relative;
  padding: 15px 25px !important;
  margin-bottom: 15px;
  border-radius: 10px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  background: #ffffff !important;
}

.event-card {
  border: 0px;
}

.event-card.clickable {
  cursor: pointer;
}

.event-card.clickable:hover {
  background-color: #f9fafb;
}

.event-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* Row 1: Icon and Companies */
.event-row-companies {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.event-icon-box {
  background-color: #F3F4F6;
  padding: 10px;
  border-radius: 8px;
  min-width: 45px;
  height: 45px;
  text-align: center;
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  align-items: center;
}

.event-icon {
  width: 22px;
  height: auto;
  object-fit: contain;
  display: block;
}

.event-companies {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  flex: 1;
  align-items: center;
}

.company-badge {
  background-color: #E5E7EB;
  color: #000000;
  padding: 5px 14px;
  border-radius: 14px;
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
  line-height: 1.4;
}

/* Row 2: Event Title */
.event-row-title {
  padding-left: 0;
  margin-top: -4px;
}

.event-name {
  font-weight: 400;
  font-size: 14px;
  margin: 0;
  line-height: 1.5;
  color: #000000;
}

/* Row 3: Date, Time, Deadline and Status */
.event-row-bottom {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 12px;
  padding-left: 0;
  margin-top: -2px;
}

.event-datetime-wrapper {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: nowrap;
  font-size: 14px;
  flex-shrink: 1;
  min-width: 0;
}

.event-calendar-icon {
  font-size: 15px;
  color: #000000;
  flex-shrink: 0;
  margin-right: 2px;
}

.event-date-time {
  font-size: 14px;
  font-weight: 400;
  color: #000000;
  white-space: nowrap;
  flex-shrink: 0;
}

.event-separator {
  color: #6B7280;
  margin: 0 6px;
  font-weight: 300;
  flex-shrink: 0;
}

.event-deadline-text {
  font-size: 14px;
  font-weight: 400;
  color: #DD3847;
  white-space: nowrap;
  flex-shrink: 0;
}

.application-status-badge {
  border-radius: 4px;
  padding: 5px 18px;
  font-size: 13px;
  font-weight: 600;
  border: none;
  min-width: 95px;
  height: 30px;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  line-height: 1;
  background-color: #E5E7EB;
  cursor: default;
  user-select: none;
}

.application-status-badge:hover {
  background-color: #E5E7EB;
  cursor: default;
}

.applied-status {
  color: #5245CC;
}

.not-applied-status {
  color: #555555;
}

/* Mobile styles */
@media (max-width: 768px) {
  .event-block {
    padding: 12px 15px !important;
  }

  .event-info {
    gap: 6px;
  }

  .event-row-companies {
    gap: 10px;
  }

  .event-icon-box {
    min-width: 40px;
    height: 40px;
    padding: 8px;
  }

  .event-icon {
    width: 20px;
    height: auto;
  }

  .event-companies {
    flex-direction: column;
    align-items: flex-start;
  }

  .company-badge {
    font-size: 14px;
    padding: 4px 12px;
    border-radius: 12px;
    white-space: normal;
    width: auto;
    word-break: break-all;
    line-height: 1.3;
  }

  .event-row-title {
    padding-left: 0;
  }

  .event-name {
    font-size: 13px;
  }

  .event-row-bottom {
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: center;
    gap: 8px;
    padding-left: 0;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  .event-datetime-wrapper {
    font-size: 13px;
    flex-shrink: 0;
    min-width: auto;
    white-space: nowrap;
    gap: 4px;
  }

  .event-date-time,
  .event-deadline-text {
    font-size: 13px;
    flex-shrink: 0;
  }
  
  .event-separator {
    margin: 0 4px;
  }

  .application-status-badge {
    width: auto;
    font-size: 12px;
    min-width: 85px;
    padding: 4px 14px;
    height: 26px;
    flex-shrink: 0;
  }
}

@media (max-width: 530px) {
  .event-block {
    padding: 10px !important;
  }

  .event-info {
    gap: 6px;
  }

  .event-icon-box {
    min-width: 36px;
    height: 36px;
    padding: 6px;
  }

  .event-icon {
    width: 18px;
    height: auto;
  }

  .company-badge {
    font-size: 12px;
    padding: 3px 10px;
    border-radius: 10px;
    white-space: normal;
    max-width: auto;
    word-break: break-all;
    line-height: 1.3;
  }

  .event-row-title {
    padding-left: 0;
  }

  .event-name {
    font-size: 12px;
  }

  .event-row-bottom {
    padding-left: 0;
    flex-wrap: nowrap;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    gap: 6px;
  }

  .event-datetime-wrapper {
    font-size: 12px;
    flex-shrink: 0;
    min-width: auto;
    gap: 3px;
  }

  .event-calendar-icon {
    font-size: 13px;
  }

  .event-date-time,
  .event-deadline-text {
    font-size: 12px;
    flex-shrink: 0;
  }
  
  .event-separator {
    margin: 0 3px;
  }
  
  .application-status-badge {
    font-size: 11px;
    min-width: 75px;
    padding: 3px 10px;
    height: 24px;
    flex-shrink: 0;
  }
}
</style>
